import { useState, useEffect } from "react";
import { BASE_URL } from "../config";
import { parseJwtExpiry, setToken, doLogout, getAuthItem } from "../utils/auth";

const API_BASE = (process.env.REACT_APP_API_BASE || BASE_URL || '').replace(/\/+$/, '');

// Public kiosk routes (check-in / device / manage) require no login and must
// never be force-logged-out by the admin session's idle/expiry timers.
const KIOSK_ROUTE_PREFIXES = ['/checkin', '/device', '/manage'];
function isKioskRoute() {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return KIOSK_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export default function useAuth() {
  const [user, setUser] = useState(null);
  const [initialized, setInitialized] = useState(false);

  const checkAuth = () => {
    setInitialized(true);
    let token = getAuthItem("token");

    // Filter out invalid token values (null, 'null', 'undefined', empty string)
    if (!token || token === 'null' || token === 'undefined' || token.trim() === '') {
      token = null;
    }

    const accountId = getAuthItem("accountId");
    const isAdmin = getAuthItem("isAdmin") === "true";
    const registrationComplete = getAuthItem("registrationComplete") === "true";

    console.log("Checking auth:", {
      token: token ? "exists" : "null",
      accountId,
      isAdmin,
      registrationComplete,
      rawIsAdmin: getAuthItem("isAdmin")
    });

    const userType = getAuthItem("userType");

    if (token && accountId) {
      const user = {
        token,
        accountId,
        userType,
        isAdmin,
        registrationComplete,
      };
      console.log("Setting user:", user);
      setUser(user);
    } else {
      console.log("No valid auth data, setting user to null");
      setUser(null);
    }
  };

  useEffect(() => {
    checkAuth();

    // Listen for storage changes (when login happens in another tab)
    const handleStorageChange = () => {
      checkAuth();
    };

    // Listen for custom auth update event (when login happens in same tab)
    const handleAuthUpdate = () => {
      checkAuth();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authUpdate', handleAuthUpdate);

    // Public kiosk routes (check-in / device / manage) have their own
    // session handling (see checkin/manageSession.js) and must never be
    // force-logged-out by the admin session's idle/expiry timers.
    if (isKioskRoute()) {
      return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('authUpdate', handleAuthUpdate);
      };
    }

    // Auto-logout / refresh timers
    let expiryTimer = null;
    let idleTimer = null;
    const IDLE_LIMIT_MS = 24 * 60 * 60 * 1000; // 24h

    const scheduleExpiryHandler = () => {
      if (expiryTimer) clearTimeout(expiryTimer);
      const token = getAuthItem('token');
      const expiry = parseJwtExpiry(token);
      if (!expiry) return;
      const now = Date.now();
      // Proactively refresh 60 seconds before expiry
      const msUntilRefresh = Math.max(0, expiry - now - 60000);
      expiryTimer = setTimeout(async () => {
        if (isKioskRoute()) return;
        try {
          const r = await fetch(API_BASE + '/auth/refresh', { method: 'POST', credentials: 'include' });
          if (r.ok) {
            const j = await r.json();
            if (j.token) setToken(j.token);
          } else {
            // refresh failed, force logout
            doLogout();
          }
        } catch (e) {
          doLogout();
        }
      }, msUntilRefresh);
    };

    // Idle logout handlers
    const resetIdle = () => {
      try { localStorage.setItem('lastActivity', String(Date.now())); } catch (_) { }
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        // Never idle-logout a route that isn't actually authenticated, and
        // never force-navigate away from the public kiosk screens.
        if (isKioskRoute() || !getAuthItem('token')) return;
        doLogout();
      }, IDLE_LIMIT_MS);
    };

    // initialize timers
    scheduleExpiryHandler();
    resetIdle();

    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    activityEvents.forEach(ev => window.addEventListener(ev, resetIdle));

    const storageListener = () => {
      scheduleExpiryHandler();
    };

    window.addEventListener('storage', storageListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authUpdate', handleAuthUpdate);
      activityEvents.forEach(ev => window.removeEventListener(ev, resetIdle));
      window.removeEventListener('storage', storageListener);
      if (expiryTimer) clearTimeout(expiryTimer);
      if (idleTimer) clearTimeout(idleTimer);
    };
  }, []);

  return user;
}